import { useState, useEffect, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getIntensity(count) {
  if (count === 0) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  return 3;
}

const COLORS = [
  "#d0d7de",
  "#9be9a8",
  "#40c463",
  "#216e39",
];

export const ActivityHeatmap = ({ userId }) => {
  const [activity, setActivity] = useState({});
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    axios.get(`${API}/gamification/activity?days=365`)
      .then(res => {
        const events = res.data || [];
        const map = {};
        for (const e of events) {
          const date = e.timestamp?.slice(0, 10);
          if (date) map[date] = (map[date] || 0) + 1;
        }
        setActivity(map);

        let s = 0;
        const today = new Date();
        for (let i = 0; i < 365; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          if (map[key]) { s++; } else if (i > 0) break;
        }
        setStreak(s);
      }).catch(() => {})
      .catch(() => {});
  }, [userId]);

  const { weeks, monthLabels } = useMemo(() => {
    const today = new Date();
    const result = [];
    const mLabels = [];
    let lastMonth = -1;

    for (let w = 51; w >= 0; w--) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(today);
        date.setDate(today.getDate() - (w * 7 + (6 - d)));
        const key = date.toISOString().slice(0, 10);
        week.push({ key, date: new Date(date) });

        if (d === 0 && date.getMonth() !== lastMonth) {
          mLabels.push({ weekIdx: 51 - w, label: MONTHS[date.getMonth()] });
          lastMonth = date.getMonth();
        }
      }
      result.push(week);
    }
    return { weeks: result, monthLabels: mLabels };
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3" data-testid="activity-heatmap">
      <div className="flex items-center justify-between mb-0.5">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-slate-700">Activity</p>
          {streak > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-bold" data-testid="streak-badge">
              {streak}d streak
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[9px] text-slate-400">
          <span>Less</span>
          {COLORS.map((c, i) => (
            <span key={i} className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
          ))}
          <span>More</span>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mb-2">Your recent actions, claims, and points earned</p>

      <div className="overflow-x-auto pb-1" data-testid="heatmap-grid-scroll">
        <div style={{ display: "grid", gridTemplateColumns: `repeat(52, 10px)`, gap: "2px", width: "fit-content" }}>
          {weeks.map((week, wi) => (
            week.map((day, di) => {
              const count = activity[day.key] || 0;
              const intensity = getIntensity(count);
              return (
                <div
                  key={day.key}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: COLORS[intensity],
                    gridColumn: wi + 1,
                    gridRow: di + 1,
                  }}
                  title={`${count} action${count !== 1 ? "s" : ""} on ${day.key}`}
                  data-testid={`heatmap-cell-${day.key}`}
                />
              );
            })
          ))}
        </div>
      </div>
    </div>
  );
};
